import { ALTAR_BACKEND_URL, apiEvents } from "./request"
import { getFingerprint } from "../lib/fingerprint"
import { trackEvent } from "../lib/analytics"

/**
 * Ocular-style streaming request handler for SSE and streaming responses
 * Based on: https://github.com/OcularEngineering/ocular/blob/main/packages/ocular-ui/services/streaming-request.ts
 *
 * @param method - HTTP method
 * @param path - API endpoint path
 * @param payload - Request body (for POST/PUT/PATCH)
 * @param queryParams - URL query parameters
 * @param cancelTokenSource - AbortController for request cancellation
 * @param token - Optional Clerk Auth Token
 * @returns Promise resolving to fetch Response object with ReadableStream body
 */
export default async function ocularStreamingRequest(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS' | 'HEAD' | 'CONNECT' | 'TRACE',
  path = '',
  payload = {},
  queryParams: Record<string, string> = {},
  cancelTokenSource: AbortController | null = null,
  token?: string | null
): Promise<Response> {
  // Sanitize path
  const sanitizedPath = path.startsWith("/") ? path : `/${path}`

  // Build URL with query params
  const url = new URL(`${ALTAR_BACKEND_URL}${sanitizedPath}`)
  Object.entries(queryParams).forEach(([key, value]) => {
    url.searchParams.append(key, value)
  })

  // Setup headers
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream', // SSE format
  }

  if (token) {
    // @ts-ignore - HeadersInit type overlap
    headers['Authorization'] = `Bearer ${token}`
  } else if (typeof window !== 'undefined') {
      // Add Fingerprint for guest usage
      try {
          const fp = await getFingerprint()
           // @ts-ignore
          headers["X-Fingerprint"] = fp
      } catch (e) {
          console.warn("Could not get fingerprint", e)
      }
  }

  // Setup fetch options
  const options: RequestInit = {
    method,
    headers,
    credentials: 'include', // Include cookies for auth
    signal: cancelTokenSource ? cancelTokenSource.signal : undefined,
  }

  // Add body for methods that support it
  if (method !== 'GET' && method !== 'HEAD') {
    options.body = JSON.stringify(payload)
  }

  // Make request
  const response = await fetch(url.toString(), options)

  // Error handling
  if (!response.ok) {
    if (response.status === 402) {
        // Dispatch Paywall Event (for guest limits)
        apiEvents.dispatchEvent(new CustomEvent("paywall-triggered"))
        trackEvent('paywall_hit', {
          trigger: 'payment_required',
          endpoint: path,
        })
        throw new Error("Payment Required") // Stop processing
    }

    if (response.status === 401) {
        throw new Error("Session expired. Please sign in again.")
    }

    if (response.status === 429) {
        // Insufficient credits - parse detail and dispatch event
        let eventDetail: Record<string, any> = {
          message: "Insufficient credits",
          creditType: undefined,
          required: 0,
          available: 0,
          upgradeUrl: "/pricing"
        }

        try {
          const errorData = await response.json()
          const detail = errorData.detail || {}

          eventDetail = {
            message: detail.message || "Insufficient credits",
            creditType: detail.credit_type || undefined,
            required: detail.required || 0,
            available: detail.available || 0,
            upgradeUrl: detail.upgrade_url || "/pricing"
          }
        } catch (parseError) {
          // Use defaults
        }

        // Dispatch event with parsed or default values
        apiEvents.dispatchEvent(new CustomEvent("insufficient-credits", {
          detail: eventDetail
        }))
        trackEvent('paywall_hit', {
          trigger: 'insufficient_credits',
          credit_type: eventDetail.creditType,
          required: eventDetail.required,
          available: eventDetail.available,
          endpoint: path,
        })

        throw new Error(eventDetail.message)
    }

    const error = await response.text()
    console.error('Streaming request error:', error)
    throw new Error(error || `HTTP error! status: ${response.status}`)
  }

  return response
}
